import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BRAND } from "../css/brand";

// Static cache to store API response globally (shared among all KpiCard instances)
let cachedData = null;
let cachePromise = null;

export default function KpiCard({
  mode = "dashboard",
  userId,
  role,
  title,
  onClick,
}) {
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  console.log("role from prop: ", role)
  console.log("usedId: ", userId)

  const defaultKpis =
    mode === "dashboard"
      ? [{ label: title, value: 0 }]
      : [
          { label: "Total", value: 0 },
          { label: "Active", value: 0 },
          { label: "Deactive", value: 0 },
        ];

  useEffect(() => {
    if (!userId || !role) return;

    const fetchCounts = async () => {
      try {
        setLoading(true);

        // ✅ Use cached response if available
        let data;
        if (cachedData) {
          data = cachedData;
        } else {
          // ✅ Prevent duplicate parallel requests (share the same Promise)
          if (!cachePromise) {
            cachePromise = axios.post(
              "https://spillas.com/api/v1/dashboard/count",
              { id: userId, role }
            );
          }
          const res = await cachePromise;
          data = res.data;
          cachedData = data; // ✅ Cache for all future components
        }

        const normalizedTitle = title?.toLowerCase().trim();

        if (mode === "dashboard") {
          let value = 0;
          switch (normalizedTitle) {
            case "key(avails)":
            case "keys (avail)":
            case "keys":
            case "key avail":
              value = data?.key_balances?.total_qty ?? 0;
              break;

            case "cnfs":
            case "cnf":
              value = data?.totals?.total_cnf?.total ?? 0;
              break;

            case "super stockists":
            case "superstockist":
            case "super stockist":
              value = data?.totals?.total_ss?.total ?? 0;
              break;

            case "distributors":
            case "distributor":
              value = data?.totals?.total_dist?.total ?? 0;
              break;

            case "micro dist.":
            case "micro distributor":
            case "fos":
              value = data?.totals?.total_fos?.total ?? 0;
              break;

            case "retailers":
            case "retailer":
              value = data?.totals?.total_retailer?.total ?? 0;
              break;

            case "customers":
              value = data?.customers?.total ?? 0;
              break;

            case "today's recommended":
              value = data?.customers?.smart?.today ?? 0;
              break;

            case "today's chalte chalte":
              value = data?.customers?.chalte_chalte?.today ?? 0;
              break;

            default:
              value = 0;
          }
          setKpis([{ label: title, value }]);
        } else {
          // ✅ Entities (Total / Active / Deactive)
          let total = 0;
          let active = 0;
          let deactive = 0;

          switch (normalizedTitle) {
            case "cnf":
              total = data?.totals?.total_cnf?.total ?? 0;
              active = data?.totals?.total_cnf?.active ?? 0;
              deactive = data?.totals?.total_cnf?.deactive ?? 0;
              break;

            case "super stockist":
            case "superstockist":
              total = data?.totals?.total_ss?.total ?? 0;
              active = data?.totals?.total_ss?.active ?? 0;
              deactive = data?.totals?.total_ss?.deactive ?? 0;
              break;

            case "distributor":
              total = data?.totals?.total_dist?.total ?? 0;
              active = data?.totals?.total_dist?.active ?? 0;
              deactive = data?.totals?.total_dist?.deactive ?? 0;
              break;

            case "micro distributor":
            case "micro dist.":
            case "fos":
              total = data?.totals?.total_fos?.total ?? 0;
              active = data?.totals?.total_fos?.active ?? 0;
              deactive = data?.totals?.total_fos?.deactive ?? 0;
              break;

            case "retailer":
              total = data?.totals?.total_retailer?.total ?? 0;
              active = data?.totals?.total_retailer?.active ?? 0;
              deactive = data?.totals?.total_retailer?.deactive ?? 0;
              break;

            default:
              break;
          }

          setKpis([
            { label: "Total", value: total },
            { label: "Active", value: active },
            { label: "Deactive", value: deactive },
          ]);
        }
      } catch (err) {
        console.error("Error fetching KPI data:", err);
        setKpis(defaultKpis);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, [userId, role, mode, title]);

  return (
    <>
      {(kpis.length ? kpis : defaultKpis).map((k, idx) => (
        <motion.div
          key={k.label}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.04 * idx }}
          onClick={() => onClick?.(k.label, k.value)}
          className="cursor-pointer rounded-2xl border bg-white p-3 shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
        >
          {/* 🔹 For dashboard — label on top, value below */}
          {mode === "dashboard" ? (
            <>
              <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">
                {k.label}
              </p>
              <div className="mt-1 h-8 flex items-center">
                {loading ? (
                  <div className="h-6 w-16 bg-slate-200 animate-pulse rounded-md"></div>
                ) : (
                  <p
                    className="text-xl sm:text-2xl font-semibold"
                    style={{ color: BRAND.darkBlue }}
                  >
                    {k.value ?? 0}
                  </p>
                )}
              </div>
            </>
          ) : (
            // 🔹 For entities — label and value side by side
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] sm:text-xs font-medium text-slate-600 m-0">
                {k.label}
              </p>
              {loading ? (
                <div className="h-4 w-8 bg-slate-200 animate-pulse rounded-md"></div>
              ) : (
                <p
                  className="text-base sm:text-lg font-semibold"
                  style={{ color: BRAND.darkBlue }}
                >
                  {k.value ?? 0}
                </p>
              )}
            </div>
          )}
        </motion.div>
      ))}
    </>
  );
}

